import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { format } from 'date-fns';
import { handleApiError, handleApiSuccess } from '../components/apiErrorHandler';
import axiosInstance from '../services/axiosInstance';

const BLANK_FORM = {
    itemId: null,
    productObj: null,
    productId: null,
    qty: 0,
    expiry: null,
    purchase_price: 0,
    selling_price: 0,
    batch_number: '',
    useExisting: true,
    deactivateExisting: false,
};

/**
 * ExceptionalInventoryDialog
 *
 * Owns exceptionalForm state, exSubmitting state, and all keyboard refs
 * internally so that typing in price/qty fields does NOT re-render InvoiceList.
 *
 * Parent controls open/close via the `visible` prop and the imperative
 * `open(mode, item, activeInfo)` method exposed via ref.
 *
 * Props:
 *   visible             — boolean
 *   onHide              — () => void  (cancel path)
 *   exceptionalActiveInfo — activeInfo passed in by parent when opening
 *   onSubmitSuccess     — (form, responseData) => void
 *   confirmWithNav      — parent's confirmWithNav helper
 */
const ExceptionalInventoryDialog = forwardRef(({
    visible,
    onHide,
    exceptionalActiveInfo,
    onSubmitSuccess,
    confirmWithNav,
}, ref) => {
    const [form, setForm]           = useState(BLANK_FORM);
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);

    const exExpiryRef   = useRef(null);
    const exPurchaseRef = useRef(null);
    const exSellingRef  = useRef(null);
    const exAddBtnRef   = useRef(null);
    const expQtyRef     = useRef(null);

    // Expose open() so the parent can populate the form without lifting state
    useImperativeHandle(ref, () => ({
        open(mode, { itemId, productObj, productId, qty }, activeInfo) {
            const isExisting = mode === 'existing';
            const purchasePrice = isExisting ? (activeInfo?.purchase_price ?? 0) : 0;
            const sellingPrice  = isExisting ? (activeInfo?.selling_price  ?? 0) : 0;
            setForm({
                itemId,
                productObj,
                productId,
                qty,
                expiry: isExisting && activeInfo?.expiry_date ? new Date(activeInfo.expiry_date) : null,
                purchase_price: purchasePrice,
                selling_price:  sellingPrice,
                batch_number:   isExisting ? (activeInfo?.batch_number || '') : '',
                useExisting:    isExisting,
                deactivateExisting: !isExisting,
            });
        },
        // Allow parent to prefill selling/purchase prices after async fetch
        setPrices(purchasePrice, sellingPrice) {
            setForm(p => ({ ...p, purchase_price: purchasePrice, selling_price: sellingPrice }));
        },
    }));

    const handleSubmit = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setSubmitting(true);

        if (!form.productId || !form.itemId) {
            handleApiError({ Error: 'Missing product or item context.' });
            submittingRef.current = false; setSubmitting(false); return;
        }
        if (!form.qty || form.qty <= 0) {
            handleApiError({ Error: 'Quantity must be greater than 0.' });
            submittingRef.current = false; setSubmitting(false); return;
        }
        if (form.productObj?.is_expiring && !form.expiry) {
            handleApiError({ Error: 'Expiry date is required.' });
            exExpiryRef.current?.focus?.();
            submittingRef.current = false; setSubmitting(false); return;
        }
        if (!form.selling_price || Number(form.selling_price) <= 0) {
            handleApiError({ Error: 'Selling price must be greater than 0.' });
            submittingRef.current = false; setSubmitting(false); return;
        }

        // Detect changes vs the active exceptional batch and confirm if needed
        if (exceptionalActiveInfo?.inventory_id && form.useExisting && !form.__skipChangePrompt) {
            const oldExpiry  = exceptionalActiveInfo.expiry_date?.split('T')[0] ?? null;
            const newExpiry  = form.expiry ? format(form.expiry, 'yyyy-MM-dd') : null;
            const changed =
                oldExpiry !== newExpiry ||
                Number(exceptionalActiveInfo.selling_price)  !== Number(form.selling_price) ||
                Number(exceptionalActiveInfo.purchase_price) !== Number(form.purchase_price) ||
                (form.batch_number && form.batch_number !== (exceptionalActiveInfo.batch_number || ''));

            if (changed) {
                const nm = form.productObj?.name || `#${form.productId}`;
                confirmWithNav({
                    header: `Update Exceptional Batch — ${nm}`,
                    message: `You changed expiry/price/batch details for ${nm}.\nUpdate the existing batch or create a NEW one (old will be deactivated)?`,
                    acceptLabel: 'Create NEW batch',
                    rejectLabel: 'Update existing',
                    accept: () => { setForm(p => ({ ...p, useExisting: false, deactivateExisting: true,  __skipChangePrompt: true })); submittingRef.current = false; setSubmitting(false); },
                    reject: () => { setForm(p => ({ ...p, useExisting: true,  deactivateExisting: false, __skipChangePrompt: true })); submittingRef.current = false; setSubmitting(false); },
                });
                return;
            }
        }

        try {
            const payload = {
                product_id:          form.productId,
                quantity:            Number(form.qty),
                expiry_date:         form.expiry ? format(form.expiry, 'yyyy-MM-dd') : null,
                purchase_price:      form.purchase_price ?? 0,
                selling_price:       Number(form.selling_price),
                use_existing:        !!form.useExisting,
                deactivate_existing: !!form.deactivateExisting,
            };
            if (form.batch_number && form.useExisting) payload.batch_number = form.batch_number;

            const res = await axiosInstance.post('/api/inventory/exceptional/create-or-topup/', payload);
            handleApiSuccess('Inventory added for old stocks.');
            onSubmitSuccess(form, res.data || {});
        } catch (err) {
            handleApiError(err);
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (submittingRef.current) return;
        onHide();
    };

    return (
        <Dialog
            header={form.useExisting ? 'Add Inventory to Old Stocks' : 'Create Exceptional Batch'}
            visible={visible}
            onHide={handleCancel}
            modal
            style={{ width: '32rem', borderRadius: '12px' }}
            closable={!submitting}
            onShow={() => {
                if (form.useExisting) setTimeout(() => exAddBtnRef.current?.focus(), 0);
                else setTimeout(() => expQtyRef.current?.focus?.(), 0);
            }}
        >
            <div className="p-fluid formgrid grid">
                <div className="field col-12">
                    <label className="font-bold">Product</label>
                    <div>{form.productObj?.name || form.productObj?.label}</div>
                </div>

                <div className="field col-6">
                    <label className="font-bold">Quantity</label>
                    <InputNumber
                        ref={expQtyRef}
                        value={form.qty}
                        onValueChange={e => setForm(p => ({ ...p, qty: e.value || 0 }))}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                if (form.productObj?.is_expiring) exExpiryRef.current?.focus?.();
                                else exPurchaseRef.current?.focus?.();
                            }
                        }}
                        inputClassName="w-full"
                        placeholder="Qty"
                        min={1}
                    />
                </div>

                <div className="field col-6">
                    <label className="font-bold">Expiry Date</label>
                    <Calendar
                        ref={exExpiryRef}
                        value={form.expiry}
                        disabled={!form.productObj?.is_expiring}
                        onChange={e => setForm(p => ({ ...p, expiry: e.value }))}
                        onSelect={() => { setTimeout(() => exPurchaseRef.current?.focus?.(), 0); }}
                        dateFormat="yy-mm-dd"
                        showIcon
                        placeholder="YYYY-MM-DD"
                        className="w-full"
                    />
                </div>

                {form.useExisting && (
                    <div className="field col-12">
                        <label className="font-bold">Batch Number (existing)</label>
                        <InputText value={form.batch_number || ''} readOnly className="w-full" />
                    </div>
                )}

                <div className="field col-6">
                    <label className="font-bold">Purchase Price (optional)</label>
                    <InputNumber
                        ref={exPurchaseRef}
                        value={form.purchase_price}
                        onValueChange={e => setForm(p => ({ ...p, purchase_price: e.value ?? 0 }))}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); exSellingRef.current?.focus?.(); }}}
                        inputClassName="w-full"
                        min={0}
                        placeholder="0"
                    />
                </div>

                <div className="field col-6">
                    <label className="font-bold">Selling Price</label>
                    <InputNumber
                        ref={exSellingRef}
                        value={form.selling_price}
                        onValueChange={e => setForm(p => ({ ...p, selling_price: e.value || 0 }))}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); exAddBtnRef.current?.focus?.(); }}}
                        inputClassName="w-full"
                        min={0}
                        placeholder="Required"
                    />
                </div>
            </div>

            <div className="flex justify-content-end gap-2 mt-3">
                <Button label="Cancel" className="p-button-text" onClick={handleCancel} disabled={submitting} />
                <Button
                    ref={exAddBtnRef}
                    label={submitting ? 'Adding...' : 'Add'}
                    icon="pi pi-check"
                    onClick={handleSubmit}
                    disabled={submitting}
                    loading={submitting}
                />
            </div>
        </Dialog>
    );
});

ExceptionalInventoryDialog.displayName = 'ExceptionalInventoryDialog';

export default ExceptionalInventoryDialog;
